import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, Shield } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProtectionStatsCards } from '@/components/cloaker/protection/ProtectionStatsCards';
import { ProtectionEmptyState } from '@/components/cloaker/protection/ProtectionEmptyState';
import { ProtectionCard } from '@/components/cloaker/protection/ProtectionCard';
import { CreateProtectionModal } from '@/components/cloaker/protection/CreateProtectionModal';
import { HowItWorksModal } from '@/components/cloaker/protection/HowItWorksModal';
import { VSLProtection } from '@/components/cloaker/protection/types';
import { VideoFolderPickerItem } from '@/components/shared/VideoFolderPicker';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { vslProtectionService } from '@/services/vslProtectionService';
import { videoService } from '@/services/videoService';

export default function VSLCloaker() {
  useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userId = user?.id || user?.id_user || user?.user_id;

  const [modalOpen, setModalOpen] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [editing, setEditing] = useState<VSLProtection | null>(null);

  // Vídeos do dashboard — alimentam o seletor do modal e enriquecem os cards
  // (o backend só guarda os ids dos vídeos da proteção).
  const { data: videos = [] } = useQuery({
    queryKey: ['dashboard-videos', userId],
    queryFn: () => videoService.getDashboardVideos(userId as string),
    enabled: !!userId,
  });

  const videoMap = useMemo(() => {
    const m = new Map<string, VideoFolderPickerItem>();
    (videos || []).forEach((v: any) =>
      m.set(v.id, { id: v.id, title: v.title, thumbnailUrl: v.thumbnailUrl }),
    );
    return m;
  }, [videos]);

  const { data: protections = [], isLoading } = useQuery({
    queryKey: ['vsl-protections', userId],
    queryFn: () => vslProtectionService.list(userId as string),
    enabled: !!userId,
  });

  const enriched: VSLProtection[] = useMemo(
    () =>
      (protections || []).map((p) => ({
        ...p,
        videoName: p.videoId ? videoMap.get(p.videoId)?.title : undefined,
        videoThumb: p.videoId ? videoMap.get(p.videoId)?.thumbnailUrl : undefined,
        redirectVideoName: p.redirectVideoId
          ? videoMap.get(p.redirectVideoId)?.title
          : undefined,
        redirectVideoThumb: p.redirectVideoId
          ? videoMap.get(p.redirectVideoId)?.thumbnailUrl
          : undefined,
      })),
    [protections, videoMap],
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['vsl-protections', userId] });

  const errMsg = (e: any) =>
    e?.response?.data?.message || e?.message || 'Tente novamente.';

  const createMutation = useMutation({
    mutationFn: (p: VSLProtection) =>
      vslProtectionService.create(userId as string, p),
    onSuccess: () => {
      invalidate();
      toast({ title: translateInterfaceText("Proteção criada") });
    },
    onError: (e: any) =>
      toast({ title: translateInterfaceText("Erro ao criar proteção"), description: errMsg(e), variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, p }: { id: string; p: VSLProtection }) =>
      vslProtectionService.update(userId as string, id, p),
    onSuccess: () => {
      invalidate();
      toast({ title: translateInterfaceText("Proteção atualizada") });
    },
    onError: (e: any) =>
      toast({ title: translateInterfaceText("Erro ao atualizar"), description: errMsg(e), variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vslProtectionService.remove(userId as string, id),
    onSuccess: () => {
      invalidate();
      toast({ title: translateInterfaceText("Proteção excluída"), variant: 'destructive' });
    },
    onError: (e: any) =>
      toast({ title: translateInterfaceText("Erro ao excluir"), description: errMsg(e), variant: 'destructive' }),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (p: VSLProtection) => {
    setEditing(p);
    setModalOpen(true);
  };
  const handleSave = (p: VSLProtection) => {
    if (p.id) updateMutation.mutate({ id: p.id, p });
    else createMutation.mutate(p);
  };
  const handleToggle = (p: VSLProtection) => {
    if (p.id) updateMutation.mutate({ id: p.id, p: { ...p, enabled: !p.enabled } });
  };
  const handleDelete = (p: VSLProtection) => {
    if (p.id) deleteMutation.mutate(p.id);
  };

  return (
    <>
      <Layout fixedSidebar>
        <div className="flex-1 flex flex-col overflow-hidden bg-[#FAFBFC]">
          <div className="h-[64px] border-b border-[#EEF0F3] bg-white flex items-center justify-between px-4 sm:px-6 lg:px-8 shrink-0">
            <div className="flex items-center gap-2.5">
              <Shield className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
              <h1 className="text-[22px] font-semibold text-[#0F172A] tracking-[-0.01em]">
                {translateInterfaceText("Proteção Anti-Plágio de VSL")}</h1>
            </div>
            {enriched.length > 0 && (
              <Button
                onClick={openCreate}
                className="bg-[#2563FF] hover:bg-[#1D4ED8] text-white h-9 px-4 rounded-lg font-bold text-[14px] shadow-[0_4px_12px_rgba(37,99,235,0.2)] transition-all"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {translateInterfaceText("Criar proteção")}</Button>
            )}
          </div>

          <main className="flex-1 overflow-y-auto">
            <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
              <ProtectionStatsCards protections={enriched} />

              {isLoading ? (
                <div className="flex items-center justify-center py-20 text-[#64748B]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> {" "}{translateInterfaceText("Carregando proteções...")}</div>
              ) : enriched.length === 0 ? (
                <ProtectionEmptyState
                  onCreate={openCreate}
                  onLearn={() => setHowItWorksOpen(true)}
                />
              ) : (
                <div className="w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                    {enriched.map((p) => (
                      <ProtectionCard
                        key={p.id}
                        protection={p}
                        onEdit={handleEdit}
                        onToggle={handleToggle}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </Layout>

      {/* Modais FORA do Layout: o Layout renderiza children 2x (mobile/desktop)
          e, como Dialog usa Portal, ficariam duplicados na tela. */}
      <CreateProtectionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSave={handleSave}
        initial={editing}
        videos={videos as VideoFolderPickerItem[]}
      />
      <HowItWorksModal open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
    </>
  );
}
